// ============================================================
// statementBits.tsx — (batch 2) small pieces shared by the settlement
// statement panel's parts (StatementPanel, statement/*): the status chip, a
// figure tile, the maker → checker step, the pencil, focusing a field by id,
// and the "{ saved: true, reload: true }" reply check.
// ============================================================
import React, { useEffect, useState } from 'react'
import { BIcon, B_ICON, money } from '../../billing/billingUi'
import type { ActionBoxItem } from '../../shared/ActionBox'
import { STATUS_LABEL, type CaseStatements, type Capacity, type StatementSummary } from '../../../types/settlementStatement.types'

export const CHIP = 'inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.06em] px-2 py-[2px] rounded-[5px] border whitespace-nowrap'
const CHIP_TONE: Record<string, string> = {
  DRAFT: 'border-casevo-border bg-casevo-input text-casevo-text-soft',
  ISSUED: 'border-casevo-brand/40 bg-casevo-mint text-casevo-brand dark:text-casevo-link',
  APPROVED: 'border-casevo-brand bg-casevo-brand text-white',
  SUPERSEDED: 'border-casevo-warn-border bg-casevo-warn-bg text-casevo-warn',
}
export const StatusChip: React.FC<{ status: StatementSummary['status'] }> = ({ status }) => <span className={`${CHIP} ${CHIP_TONE[status]}`}>{STATUS_LABEL[status]}</span>
export const stRef = (s: Pick<StatementSummary, 'number' | 'version'>): string => `${s.number ?? 'Draft'} v${s.version}`
export const shortHash = (h: string | null): string => (h ? `${h.slice(0, 8)}…` : '—')

// (E-sign fixes B) A row of figure tiles that wraps by the room it has (not a
// fixed 3 columns): each tile grows from ~8.5rem, so at 360 px / 200% zoom a
// tile that doesn't fit drops to its own full-width row — no clipped £ amount,
// no sideways scroll.
export const FIGURE_GRID = 'flex flex-wrap gap-[10px] [&>*]:flex-[1_1_8.5rem] [&>*]:min-w-0'
// A figure tile; `sub` sits under the amount (e.g. where the net sum goes).
// Long labels / amounts wrap inside the tile rather than overflow it.
export const Figure: React.FC<{ label: string; pence: number; strong?: boolean; sub?: React.ReactNode }> = ({ label, pence, strong, sub }) => (
  <div className={`min-w-0 rounded-[10px] border px-[12px] py-[8px] ${strong ? 'border-casevo-brand/40 bg-casevo-mint' : 'border-casevo-border bg-casevo-input'}`} data-figure={label}>
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-casevo-text-soft break-words">{label}</div>
    <div className={`tabular-nums [overflow-wrap:anywhere] ${strong ? 'text-[17px] font-bold' : 'text-[15px] font-semibold'} text-casevo-forest`}>{money(pence)}</div>
    {sub && <div className="text-[12px] leading-[1.35] text-casevo-text-soft break-words">{sub}</div>}
  </div>
)

export const Step: React.FC<{ done: boolean; now?: boolean; label: string }> = ({ done, now = false, label }) => (
  <span className={`inline-flex items-center gap-[5px] whitespace-nowrap text-[13px] ${done ? 'text-casevo-brand dark:text-casevo-link font-semibold' : 'text-casevo-text-soft'}`}>
    <span className={`w-[16px] h-[16px] shrink-0 rounded-full grid place-items-center border ${done ? 'bg-casevo-brand border-casevo-brand text-white' : 'border-casevo-border'}`}>{done && <BIcon name="check" size={10} strokeWidth={3} />}</span>
    <span className={now ? '' : 'sr-only sm:not-sr-only'}>{label}</span><span className="sr-only">{done ? ', done' : ', not done'}</span>
  </span>
)

// (Settlement redesign) The step trail, at the foot of the action bar: always one
// line, so the bar stays slim. On a phone it's the dots with the current step named.
// Who did each step is in History.
export const StepTrail: React.FC<{ trail: { label: string; done: boolean; now: boolean }[] }> = ({ trail }) => (
  <ol aria-label="Steps" data-step-trail="" className="m-0 p-0 list-none flex flex-nowrap items-center gap-x-[6px] sm:gap-x-[4px]">
    {trail.map((t, i) => (
      <li key={t.label} className="inline-flex items-center gap-x-[4px]" aria-current={t.now ? 'step' : undefined}>
        {i > 0 && <span className="hidden sm:inline text-casevo-text-soft" aria-hidden="true">→</span>}
        <Step done={t.done} now={t.now} label={t.label} />
      </li>
    ))}
  </ol>
)

// (Settlement redesign) What stands in the way of the next step: compact rows at the
// top of the action bar, directly above the main button. Two lines at most each
// (the whole reason is still read out, and shows on hover).
export const BarRows: React.FC<{ items: ActionBoxItem[]; ariaLabel: string; title?: string; live?: boolean }> = ({ items, ariaLabel, title, live = false }) => (
  <div role={live ? 'status' : 'group'} aria-label={ariaLabel} className="max-h-[30vh] overflow-y-auto flex flex-col gap-[4px]" data-bar-rows="">
    {title && <p className="m-0 text-[13px] font-semibold text-casevo-danger">{title}</p>}
    {items.map((it) => (
      <div key={it.key} className="flex items-baseline justify-between gap-[12px] text-[13px] text-casevo-forest">
        <span className="min-w-0 line-clamp-2" title={typeof it.message === 'string' ? it.message : undefined}>{it.message}{it.hint ? <span className="text-casevo-text-soft"> {it.hint}</span> : null}</span>
        {it.actions?.map((a) => <button key={a.label} type="button" className="shrink-0 text-[13px] font-semibold text-casevo-link whitespace-nowrap focus:outline-none casevo-focus rounded-[4px] hover:underline underline-offset-2 disabled:opacity-50" disabled={a.disabled} onClick={a.onClick}>{a.busy ? a.busyLabel ?? a.label : a.label}{a.arrow && <span aria-hidden="true"> →</span>}</button>)}
      </div>
    ))}
  </div>
)

// (Settlement redesign) A fold that stays mounted while it's shut, so its live
// parts (an open e-sign action, a focus target) aren't torn down.
export const Fold: React.FC<{ label: React.ReactNode; className?: string; labelClass?: string; children: React.ReactNode; ariaLabel?: string; dataKey?: string; openKey?: number }> = ({ label, className = '', labelClass = '', children, ariaLabel, dataKey, openKey = 0 }) => {
  const [open, setOpen] = useState(false)
  useEffect(() => { if (openKey > 0) setOpen(true) }, [openKey])
  return (
    <div className={className} data-fold={dataKey ?? ''} data-open={open ? 'true' : 'false'}>
      <button type="button" className={`w-full text-left flex items-center gap-[6px] focus:outline-none casevo-focus rounded-[4px] ${labelClass}`} aria-expanded={open} aria-label={ariaLabel} onClick={() => setOpen((o) => !o)}>
        <BIcon name="chevr" size={12} className={`shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} /><span className="min-w-0">{label}</span>
      </button>
      <div className={open ? 'mt-3' : 'hidden'}>{children}</div>
    </div>
  )
}

// The read-first pencil: a summary's one way into its editor.
export const EditPencil: React.FC<{ label: string; onClick: () => void; disabled?: boolean }> = ({ label, onClick, disabled }) => (
  <button type="button" className={B_ICON} aria-label={label} title={label} disabled={disabled} onClick={onClick}><BIcon name="pen" size={14} /></button>
)

// A read-only "label: value" row of a summary.
export const SummaryRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[12px] font-semibold text-casevo-text-soft">{label}</dt>
    <dd className="m-0 text-[13.5px] text-casevo-forest break-words">{children}</dd>
  </div>
)

// Focus the field with this id (or the first input / select inside it — a
// DateInput's day box), and bring it into view. False when it isn't there.
export const focusById = (id: string): boolean => {
  const el = document.getElementById(id)
  if (!el) return false
  const target = el.matches('input, select, textarea, button') ? el : el.querySelector<HTMLElement>('input, select, textarea')
  if (!target) return false
  target.focus()
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return true
}

// (Batch 2) A write may answer `{ saved: true, reload: true }` (saved, but the
// fresh list couldn't be built) — the panel re-reads.
export const isReloadReply = (r: unknown): boolean =>
  !!r && typeof r === 'object' && (r as { reload?: unknown }).reload === true && !('statements' in (r as object))

// A child / protected party? From the draft, else the case's latest
// statement, else a recorded litigation friend; null = not known (a new
// statement's payee is then left to the server).
export const knownChildOrPp = (capacity: Capacity | null | undefined, data: CaseStatements): boolean | null => {
  const c = capacity ?? data.statements[0]?.capacity ?? null
  if (c) return c !== 'ADULT'
  return data.litigationFriendName ? true : null
}
